import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Chat as ChatIcon,
  Sort as SortIcon,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TicketChat from './TicketChat';
import { useSelector, useDispatch } from 'react-redux';
import { setSearchResults } from '../../redux/slices/searchSlice';

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'low':
      return 'success';
    case 'medium':
      return 'warning';
    case 'high':
      return 'error';
    case 'critical':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return 'info';
    case 'in_progress':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'default';
    default:
      return 'default';
  }
};

const getPriorityValue = (priority) => {
  switch (priority) {
    case 'critical':
      return 4;
    case 'high':
      return 3;
    case 'medium':
      return 2;
    case 'low':
      return 1;
    default:
      return 0;
  }
};

const getStatusValue = (status) => {
  switch (status) {
    case 'open':
      return 1;
    case 'in_progress':
      return 2;
    case 'resolved':
      return 3;
    case 'closed':
      return 4;
    default:
      return 0;
  }
};

const TicketList = ({ tickets, onEdit, accounts, users, searchTerm = '' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const { results } = useSelector((state) => state.search);

  const getAccountName = useCallback(
    (accountId) => {
      const account = accounts?.find((acc) => acc.id === accountId);
      return account?.name || accountId;
    },
    [accounts]
  );

  const getUserName = useCallback(
    (userId) => {
      if (!userId) return t('tickets.unassigned');
      const user = users?.find((u) => u.id === userId);
      return user?.displayName || user?.email || t('tickets.unassigned');
    },
    [users, t]
  );

  useEffect(() => {
    if (searchTerm) {
      const filteredTickets = tickets.filter((ticket) => {
        const searchLower = searchTerm.toLowerCase();
        const ticketName = ticket.name?.toLowerCase() || '';
        const ticketDescription = ticket.description?.toLowerCase() || '';
        const accountName =
          getAccountName(ticket.accountId)?.toLowerCase() || '';
        const assignedToName =
          getUserName(ticket.assignedTo)?.toLowerCase() || '';
        const tags = ticket.tags?.join(' ').toLowerCase() || '';

        return (
          ticketName.includes(searchLower) ||
          ticketDescription.includes(searchLower) ||
          accountName.includes(searchLower) ||
          assignedToName.includes(searchLower) ||
          tags.includes(searchLower)
        );
      });
      dispatch(
        setSearchResults({ module: 'tickets', results: filteredTickets })
      );
    } else {
      dispatch(setSearchResults({ module: 'tickets', results: tickets }));
    }
  }, [searchTerm, tickets, dispatch, getAccountName, getUserName]);

  const handleRowClick = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortTickets = (tickets) => {
    if (!sortField) return tickets;

    return [...tickets].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'priority':
          comparison =
            getPriorityValue(a.priority) - getPriorityValue(b.priority);
          break;
        case 'status':
          comparison = getStatusValue(a.status) - getStatusValue(b.status);
          break;
        case 'account':
          comparison = getAccountName(a.accountId).localeCompare(
            getAccountName(b.accountId)
          );
          break;
        case 'assignedTo':
          comparison = getUserName(a.assignedTo).localeCompare(
            getUserName(b.assignedTo)
          );
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const displayedTickets = useMemo(() => {
    if (!searchTerm) return tickets;

    return tickets.filter((ticket) => {
      const searchLower = searchTerm.toLowerCase();
      const ticketName = ticket.name?.toLowerCase() || '';
      const ticketDescription = ticket.description?.toLowerCase() || '';
      const accountName = getAccountName(ticket.accountId)?.toLowerCase() || '';
      const assignedToName =
        getUserName(ticket.assignedTo)?.toLowerCase() || '';
      const tags = ticket.tags?.join(' ').toLowerCase() || '';

      return (
        ticketName.includes(searchLower) ||
        ticketDescription.includes(searchLower) ||
        accountName.includes(searchLower) ||
        assignedToName.includes(searchLower) ||
        tags.includes(searchLower)
      );
    });
  }, [tickets, searchTerm, getAccountName, getUserName]);

  const sortedTickets = sortTickets(displayedTickets);

  const SortableHeader = ({ field, children }) => (
    <TableCell
      onClick={() => handleSort(field)}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
        whiteSpace: 'nowrap',
        position: 'relative',
        paddingRight: '24px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {children}
        {sortField === field && (
          <Box sx={{ position: 'absolute', right: 4 }}>
            {sortDirection === 'asc' ? (
              <ArrowUpward fontSize="small" />
            ) : (
              <ArrowDownward fontSize="small" />
            )}
          </Box>
        )}
      </Box>
    </TableCell>
  );

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={selectedTicket ? 6 : 12}
        sx={{ width: selectedTicket ? '50%' : '100%' }}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeader field="name">
                  {t('tickets.name')}
                </SortableHeader>
                <SortableHeader field="priority">
                  {t('tickets.priority')}
                </SortableHeader>
                <SortableHeader field="status">
                  {t('tickets.status')}
                </SortableHeader>
                <SortableHeader field="account">
                  {t('tickets.account')}
                </SortableHeader>
                <SortableHeader field="assignedTo">
                  {t('tickets.assignedTo')}
                </SortableHeader>
                <TableCell>{t('tickets.tags')}</TableCell>
                <TableCell>{t('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t('tickets.noTicketsFound')}
                  </TableCell>
                </TableRow>
              ) : (
                sortedTickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    onClick={() => handleRowClick(ticket)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>{ticket.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(`tickets.priorities.${ticket.priority}`)}
                        color={getPriorityColor(ticket.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`tickets.statuses.${ticket.status}`)}
                        color={getStatusColor(ticket.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{getAccountName(ticket.accountId)}</TableCell>
                    <TableCell>{getUserName(ticket.assignedTo)}</TableCell>
                    <TableCell>
                      {ticket.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('app.edit')}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(ticket);
                          }}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      {selectedTicket && (
        <Grid item xs={6} sx={{ width: '48%', height: '77vh' }}>
          <Paper sx={{ height: '100%', p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {t('tickets.comments')} - {selectedTicket.name}
              </Typography>
              <IconButton onClick={() => setSelectedTicket(null)} size="small">
                <ChatIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TicketChat ticket={selectedTicket} />
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default TicketList;
